/**
 * WhatsAppImagePicker Component
 * Griglia di immagini dalla chat WhatsApp per allegarle alle task
 */

import { useState, useEffect, useCallback } from 'react';
import { Check, Loader2, Image, X } from 'lucide-react';
import { useWhatsAppChatStore } from '@/lib/stores/whatsappChatStore';
import { callWahaProxy } from '@/lib/services/wahaProxyService';

interface WhatsAppImagePickerProps {
  chatId: string;
  onSelect: (files: File[]) => void;
  onClose: () => void;
}

interface ImageItem {
  id: string;
  url: string;
  timestamp: number;
  senderName?: string;
}

export function WhatsAppImagePicker({ chatId, onSelect, onClose }: WhatsAppImagePickerProps) {
  const [images, setImages] = useState<ImageItem[]>([]);
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [loading, setLoading] = useState(true);
  const [downloading, setDownloading] = useState(false);

  const { wahaConfig } = useWhatsAppChatStore();

  useEffect(() => {
    const { messages } = useWhatsAppChatStore.getState();
    const imageMessages = messages
      .filter(m => m.type === 'image' && m.mediaUrl)
      .sort((a, b) => (b.timestamp || 0) - (a.timestamp || 0))
      .slice(0, 20)
      .map(m => ({
        id: m.id,
        url: m.mediaUrl!,
        timestamp: m.timestamp || 0,
        senderName: m.senderName || m.from?.split('@')[0],
      }));
    setImages(imageMessages);
    setLoading(false);
  }, [chatId]);

  const handleToggle = useCallback((id: string) => {
    setSelectedIds(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }, []);

  const handleConfirm = useCallback(async () => {
    if (selectedIds.size === 0) return;
    setDownloading(true);

    try {
      const files: File[] = [];
      for (const id of selectedIds) {
        const img = images.find(i => i.id === id);
        if (!img) continue;

        try {
          const url = img.url;
          let response: Response;

          const isWahaUrl = url.includes('/api/files/') || url.includes('/api/media/') ||
            (wahaConfig?.apiUrl && url.startsWith(wahaConfig.apiUrl));

          if (isWahaUrl) {
            const urlObj = new URL(url);
            const path = urlObj.pathname + urlObj.search;
            response = await callWahaProxy({ method: 'GET', path });
          } else {
            response = await fetch(url);
          }

          if (!response.ok) continue;

          const blob = await response.blob();
          const filename = `whatsapp-image-${id.substring(0, 8)}.jpg`;
          files.push(new File([blob], filename, { type: blob.type || 'image/jpeg' }));
        } catch (err) {
          console.error('[WhatsAppImagePicker] Error downloading image:', id, err);
        }
      }

      if (files.length > 0) {
        onSelect(files);
      }
      onClose();
    } catch (err) {
      console.error('[WhatsAppImagePicker] Error:', err);
    } finally {
      setDownloading(false);
    }
  }, [selectedIds, images, wahaConfig, onSelect, onClose]);

  if (loading) {
    return (
      <div className="flex items-center justify-center py-4">
        <Loader2 className="w-5 h-5 animate-spin text-[#787774]" />
      </div>
    );
  }

  if (images.length === 0) {
    return (
      <div className="text-center py-4">
        <Image className="w-8 h-8 text-[#787774] mx-auto mb-2" />
        <p className="text-xs text-[#787774]">Nessuna immagine nella chat</p>
      </div>
    );
  }

  return (
    <div className="border border-[rgba(55,53,47,0.09)] dark:border-white/10 rounded-lg overflow-hidden">
      {/* Header */}
      <div className="flex items-center justify-between px-3 py-2 bg-[#F7F7F5] dark:bg-gray-800 border-b border-[rgba(55,53,47,0.09)] dark:border-white/10">
        <span className="text-xs font-medium text-[#37352F] dark:text-white">
          Immagini dalla chat
        </span>
        <button onClick={onClose} className="p-0.5 rounded hover:bg-[rgba(55,53,47,0.08)] dark:hover:bg-white/10">
          <X className="w-3.5 h-3.5 text-[#787774]" />
        </button>
      </div>

      {/* Grid */}
      <div className="grid grid-cols-4 gap-1 p-2 max-h-[200px] overflow-y-auto">
        {images.map((img) => {
          const isSelected = selectedIds.has(img.id);
          return (
            <button
              key={img.id}
              onClick={() => handleToggle(img.id)}
              className={`relative aspect-square rounded overflow-hidden border-2 transition-colors ${
                isSelected ? 'border-blue-500' : 'border-transparent hover:border-[rgba(55,53,47,0.16)]'
              }`}
            >
              <img
                src={img.url}
                alt=""
                className="w-full h-full object-cover"
                loading="lazy"
              />
              {isSelected && (
                <div className="absolute inset-0 bg-blue-500/20 flex items-center justify-center">
                  <div className="w-5 h-5 rounded-full bg-blue-500 flex items-center justify-center">
                    <Check className="w-3 h-3 text-white" />
                  </div>
                </div>
              )}
            </button>
          );
        })}
      </div>

      {/* Footer */}
      {selectedIds.size > 0 && (
        <div className="px-3 py-2 border-t border-[rgba(55,53,47,0.09)] dark:border-white/10">
          <button
            onClick={handleConfirm}
            disabled={downloading}
            className="w-full flex items-center justify-center gap-1.5 px-3 py-1.5 text-xs font-medium text-white bg-blue-600 hover:bg-blue-700 disabled:opacity-50 rounded-lg transition-colors"
          >
            {downloading ? (
              <Loader2 className="w-3.5 h-3.5 animate-spin" />
            ) : null}
            {downloading ? 'Scaricamento...' : `Aggiungi ${selectedIds.size} immagin${selectedIds.size === 1 ? 'e' : 'i'}`}
          </button>
        </div>
      )}
    </div>
  );
}
